import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    Caption,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";

type SetModifyCoachesData = {
    setModifyCoachesCapability?:
        | {
              target: {
                  id: string;
                  canModifyCoaches: boolean;
              };
              error?: undefined;
          }
        | {
              target?: undefined;
              error: {
                  code: string;
              };
          };
};

type SetModifyCoachesVariables = {
    targetKaid: string;
    allow: boolean;
};

const SET_MODIFY_COACHES_MUTATION: GqlOperation<
    SetModifyCoachesData,
    SetModifyCoachesVariables
> = gqlOp(gql`
    mutation EditModifyCoachesCapability(
        $targetKaid: String!
        $allow: Boolean!
    ) {
        setModifyCoachesCapability(targetKaid: $targetKaid, allow: $allow) {
            target {
                id
                canModifyCoaches
            }
            error {
                code
            }
        }
    }
`);

type State = {
    checked: boolean;
    loading: boolean;
    modifyCoachesError: string | null | undefined;
};

type OwnProps = {
    targetId: string;
    canModifyCoaches: boolean;
    profileRoot: string;
    successfullyChangedSettings: () => unknown;
};

type Props = OwnProps & WithApolloProps;

class CoachesSection extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_mounted' has no initializer and is not definitely assigned in the constructor.
    _mounted: boolean;
    state: State = {
        checked: this.props.canModifyCoaches,
        loading: false,
        modifyCoachesError: null,
    };

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    safeSetState(newState: Partial<State>, callback?: () => unknown) {
        if (!this._mounted) {
            return;
        }
        // @ts-expect-error - TS2345 - Argument of type 'Partial<State>' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
        this.setState(newState, callback);
    }

    onChangeModifyCoaches: (canModifyChanges: boolean) => void = (
        canModifyChanges: boolean,
    ) => {
        const {targetId, successfullyChangedSettings, gqlClient} = this.props;
        this.safeSetState({
            checked: canModifyChanges,
            loading: true,
            modifyCoachesError: null,
        });

        gqlClient
            .mutate(SET_MODIFY_COACHES_MUTATION, {
                variables: {
                    targetKaid: targetId,
                    allow: canModifyChanges,
                },
            })
            .then(({data}) => {
                const error = data?.setModifyCoachesCapability?.error;

                this.safeSetState({loading: false});

                if (error) {
                    this.safeSetState({
                        modifyCoachesError: t`Your setting could not be saved`,
                        checked: !this.state.checked,
                    });
                } else {
                    successfullyChangedSettings();
                }
            })
            .catch((error) => {
                this.safeSetState({
                    modifyCoachesError: t`Your setting could not be saved`,
                    checked: !this.state.checked,
                    loading: false,
                });
            });
    };

    render(): React.ReactNode {
        const {profileRoot} = this.props;
        const {checked, loading, modifyCoachesError} = this.state;
        return (
            <React.Fragment>
                <HeadingSmall style={styles.header} tag="h2">
                    {t`Coaches`}
                </HeadingSmall>
                <Strut size={spacing.medium_16} />
                <Body>
                    {t`You control whether your child can accept invitations to be "coached." Your child's coaches are able to view all of their activity. A child can add a coach by either entering the class code given to them by a classroom teacher, or by entering their teacher's email. This setting should be set to ON to allow your child to use Khan Academy in the classroom with a teacher. Your child's coaches are able to view all of their activity.`}
                </Body>
                <Strut size={spacing.small_12} />
                <View style={styles.row}>
                    <LabelMedium htmlFor="toggle-switch-canModifyCoaches">
                        {t`Can add other coaches`}
                    </LabelMedium>
                    <Strut size={spacing.medium_16} />
                    <Checkbox
                        id="toggle-switch-canModifyCoaches"
                        checked={checked}
                        onChange={this.onChangeModifyCoaches}
                        style={styles.checkbox}
                        disabled={loading}
                    />
                    <Strut size={spacing.medium_16} />
                    <div aria-live="polite">
                        {modifyCoachesError != null && (
                            <Caption style={styles.error}>
                                {modifyCoachesError}
                            </Caption>
                        )}
                    </div>
                </View>
                <Strut size={spacing.small_12} />
                <Body>
                    <Trans>
                        You can always monitor your child's coaches by visiting
                        the{" "}
                        <Link href={profileRoot + "coaches"}>
                            "Coaches" tab on your child's profile
                        </Link>{" "}
                        to view their current list of coaches and coach
                        invitations.
                    </Trans>
                </Body>
            </React.Fragment>
        );
    }
}

type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof CoachesSection,
        React.ComponentProps<typeof CoachesSection>
    >
>;
export default withApollo(CoachesSection) as React.ComponentType<ExportProps>;

const styles = StyleSheet.create({
    checkbox: {
        // This centers the checkbox with the first line of text.
        // (Line height - checkbox height) / 2 = (20 - 16) / 2 = 2
        marginTop: 2,
    },
    header: {
        color: color.offBlack,
    },
    row: {
        flexDirection: "row",
    },
    error: {
        color: color.red,
    },
});
